<template>
  <div
    :class="['gear-loader', { 'gear-loader--overlay': overlay }]"
    role="status"
    aria-live="polite"
  >
    <div class="gear-loader__inner">
      <svg
        class="gear-loader__gear"
        viewBox="-55 -55 110 110"
        :width="size"
        :height="size"
        aria-hidden="true"
        focusable="false"
      >
        <path class="gear-loader__track" :d="gearPath" />
        <path class="gear-loader__draw" pathLength="100" :d="gearPath" />
      </svg>
      <span v-if="showLogo" class="gear-loader__logo" aria-hidden="true">
        GoFindBuild
      </span>
    </div>
    <span class="visually-hidden">{{ label }}</span>
  </div>
</template>

<script>
// Turning-gear loader from the marketing site (GFB-1418). Spins for as long
// as it's rendered — the parent removes it (v-if) when loading finishes.
const GEAR_PATH =
  "M39.1 -8.3 L49.6 -6.3 L49.6 6.3 L39.1 8.3 L35.3 18.8 L42.0 27.1 L34.0 36.7 L24.6 31.5 L15.0 37.1 L14.8 47.8 L2.4 49.9 L-1.4 40.0 L-12.4 38.0 L-19.4 46.1 L-30.2 39.8 L-26.8 29.7 L-33.9 21.2 L-44.5 22.9 L-48.8 11.1 L-39.6 5.6 L-39.6 -5.6 L-48.8 -11.1 L-44.5 -22.9 L-33.9 -21.2 L-26.8 -29.7 L-30.2 -39.8 L-19.4 -46.1 L-12.4 -38.0 L-1.4 -40.0 L2.4 -49.9 L14.8 -47.8 L15.0 -37.1 L24.6 -31.5 L34.0 -36.7 L42.0 -27.1 L35.3 -18.8 Z M17 0 A17 17 0 1 0 -17 0 A17 17 0 1 0 17 0 Z"

export default {
  name: "GearLoader",
  props: {
    size: {
      type: Number,
      default: 44,
    },
    label: {
      type: String,
      default: "Loading",
    },
    showLogo: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      gearPath: GEAR_PATH,
    }
  },
}
</script>

<style lang="scss" scoped>
$gear-orange: var(--gfb-orange-brand-500, #f16c0e);
$gear-track: #f3e3d6;
$gear-cycle: 2.6s;

.gear-loader {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.gear-loader--overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  background: #fff;
}

.gear-loader__inner {
  display: flex;
  align-items: center;
  gap: 14px;
}

.gear-loader__gear {
  display: block;
  flex-shrink: 0;
  overflow: visible;
  animation: gear-loader-turn $gear-cycle linear infinite;

  path {
    fill-rule: evenodd;
    stroke-width: 5; // 5 viewBox units = 2px at 44px; scales with size
    stroke-linejoin: round;
  }
}

.gear-loader__track {
  fill: none;
  stroke: $gear-track;
}

.gear-loader__draw {
  fill: $gear-orange;
  fill-opacity: 0;
  stroke: $gear-orange;
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: gear-loader-draw $gear-cycle ease-in-out infinite;
}

.gear-loader__logo {
  font-family: "Source Serif 4", Georgia, serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 1;
  color: #16243d;
}

@keyframes gear-loader-turn {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// 0–60% draw the outline, 60–75% fill, 75–90% hold, 90–100% fade back to
// the start state. The cycle restarts with nothing drawn, so there's no jump.
@keyframes gear-loader-draw {
  0% {
    stroke-dashoffset: 100;
    stroke-opacity: 1;
    fill-opacity: 0;
  }
  60% {
    stroke-dashoffset: 0;
    fill-opacity: 0;
  }
  75% {
    fill-opacity: 1;
  }
  90% {
    stroke-dashoffset: 0;
    stroke-opacity: 1;
    fill-opacity: 1;
  }
  100% {
    stroke-dashoffset: 0;
    stroke-opacity: 0;
    fill-opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .gear-loader__gear,
  .gear-loader__draw {
    animation: none;
  }

  .gear-loader__draw {
    stroke-dashoffset: 0;
    fill-opacity: 1;
  }
}
</style>
